import { computed, onMounted, ref } from 'vue';
import { isNil } from 'lodash-es';
import router from '@/common/router';
import { USER_GENERAL_SETTING_ITEMS } from '@/common/utils';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import type { UpdateUserV7Request } from '@/api-client/userAndPermission/model';
import {
  getGetV7MeQueryKey,
  usePatchV7Me,
} from '@/api-client/userAndPermission/queries/user-v7-controller/user-v7-controller';
import { checkRole } from '@/common/auth/auth.utils';
import { queryClient } from '@/common/lib/queryClient';
import { normalizeUserLanguage } from '@/common/locale';
import type { UserProfile, GroupItem, UserTheme, OpenedSubPanel } from './myInfoPopup.types';
import { THEME_MAPPER, THEME_START_CASE } from './myInfoPopup.define';
import { useMyInfoStore } from '@/config/stores/my-info';
import { storeToRefs } from 'pinia';

const DEFAULT_LANGUAGE =
  USER_GENERAL_SETTING_ITEMS.lang.find((item) => item.value === 'EN') ??
  ({ name: 'English', value: 'EN' } as const);

export const useMyInfoData = () => {
  const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

  const getLanguage = (field: keyof (typeof USER_GENERAL_SETTING_ITEMS.lang)[0], value: string) =>
    USER_GENERAL_SETTING_ITEMS.lang.find((item) => item[field] === value) ?? DEFAULT_LANGUAGE;

  const getTheme = (value?: string) =>
    USER_GENERAL_SETTING_ITEMS.theme.find((item) => item.value === value) ??
    USER_GENERAL_SETTING_ITEMS.theme[0];

  const userProfile = computed<UserProfile>(() => ({
    email: userInfo.value.email,
    name: userInfo.value.name,
    profileImage: userInfo.value.profileImage,
    role: userInfo.value.role,
  }));
  const userTheme = computed<UserTheme>(() => getTheme(userInfo.value.theme).name as UserTheme);
  const userLanguage = computed<string>(
    () => getLanguage('value', normalizeUserLanguage(userInfo.value.language)).name,
  );
  const groupItems = computed<GroupItem[]>(() =>
    userInfo.value.groups.map((item) => ({
      value: item.groupId,
      name: item.name ?? `Undefined Group ${item.groupId}`,
    })),
  );

  const languageInfo = ref<string>(userLanguage.value);
  const themeInfo = ref<UserTheme>(THEME_START_CASE[0]);
  const selectedGroup = ref<GroupItem | null>(null);
  const openedSubPanel = ref<OpenedSubPanel>(null);

  const { mutateAsync: patchMyInfo } = usePatchV7Me({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetV7MeQueryKey({}),
        });
      },
    },
    request: {
      frameName: FRAME_NAMES.CONFIG_MY_INFO.PATCH,
    },
  });

  const updateMyInfo = async () => {
    const language = getLanguage('name', languageInfo.value).value;
    const theme = THEME_MAPPER[themeInfo.value];
    const request: UpdateUserV7Request = {
      ...userInfo.value,
      theme,
      language,
      employeeId: userInfo.value.employeeId?.trim() || undefined,
      email: userInfo.value.email,
      name: userInfo.value.name,
    };

    if (!checkRole.isMaster(userInfo.value.role)) {
      request.groups = userInfo.value.groups.map((item) => {
        item.fixed = item.groupId === selectedGroup.value?.value;
        return item;
      });
    }

    await patchMyInfo(
      { data: request },
      {
        onSuccess: () => {
          router.go(0);
        },
      },
    );
  };

  const changeGroup = async (group: GroupItem) => {
    if (group.value === selectedGroup.value?.value) {
      return;
    }
    selectedGroup.value = group;
    await updateMyInfo();
  };

  const changeLanguage = async (language?: string) => {
    if (!language || language === languageInfo.value) {
      return;
    }
    languageInfo.value = language;
    await updateMyInfo();
  };

  const changeTheme = async (theme?: string) => {
    if (!theme || theme === themeInfo.value) {
      return;
    }
    themeInfo.value = theme as UserTheme;
    await updateMyInfo();
  };

  onMounted(() => {
    languageInfo.value = userLanguage.value;
    themeInfo.value = userTheme.value;
    const currentGroup = userInfo.value.groups.find((item) => item.fixed);
    if (currentGroup && currentGroup.name && !isNil(currentGroup.groupId)) {
      selectedGroup.value = { name: currentGroup.name, value: currentGroup.groupId };
    } else {
      selectedGroup.value = null;
    }
  });

  return {
    userProfile,
    languageInfo,
    themeInfo,
    groupItems,

    selectedGroup,
    openedSubPanel,

    changeGroup,
    changeLanguage,
    changeTheme,
  };
};
